import ReactGA from "react-ga4";
import { getPageTitle } from "./pageTitles";
import { ga4TrackingId } from ".";
import Api from "../openapi";
import { DeviceInfoRequest } from "../openapi/client";
import { getUserAgentDetails } from "../components/App/userAgent";
import { getAccessToken } from "./tokenStore";

export const trackRegistrationStep = (flow_name: "find_best_plan" | "join_now", step_name: string) => {
  trackEvent("registration", { flow_name, step_name });
};

export const trackSaleStep = (step_name: string) => {
  trackEvent("sale", { flow_name: "plan_sale", step_name });
};

// Initialize Google Analytics
export const initializeGA = () => {
  const trackingId = ga4TrackingId;

  if (trackingId) {
    ReactGA.initialize(trackingId);
    console.log("Google Analytics 4 initialized with tracking ID:", trackingId);
  } else {
    console.warn("Google Analytics 4 tracking ID not found or not set properly in environment variables");
  }
};

// Send pageview
export const sendPageview = (page: string, title?: string) => {
  ReactGA.send({
    hitType: "pageview",
    page,
    title: title || document.title,
  });
};

export const sendEvent = (eventName: string, parameters?: Record<string, any>) => {
  if ((window as any).dataLayer) {
    (window as any).dataLayer.push({
      event: eventName,
      ...parameters,
    });
  }
};

export const sendCustomEvent = (eventName: string, category: string, parameters?: Record<string, any>) => {
  ReactGA.event({
    category,
    action: eventName,
    ...parameters,
  });
};

const getUserId = (user: any): string | undefined => {
  // Prefer actual user ID if available
  if (user?.id) {
    return user.id;
  }

  return undefined;
};

export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  sendEvent(eventName, parameters);
  console.log(
    `Analytics: Event tracked - ${eventName}`,
    parameters ? `with parameters: ${JSON.stringify(parameters)}` : "",
  );
};

export const trackCustomEvent = (eventName: string, category: string, parameters?: Record<string, any>) => {
  sendCustomEvent(eventName, category, parameters);
  console.log(
    `Analytics: Custom event tracked - ${eventName}`,
    parameters ? `with parameters: ${JSON.stringify(parameters)}` : "",
  );
};
export const trackLoginSuccess = (user?: any) => {
  const eventData: any = {
    method: "password",
  };

  const userId = getUserId(user);
  if (userId) {
    eventData.userId = userId;
  }

  sendEvent("login", eventData);
  console.log("Analytics: Login success tracked", userId ? `with userId: ${userId}` : "without userId");
};

export const trackRegistrationSuccess = (method: "stepper" | "quick_register", user?: any) => {
  const eventData: any = {};

  const userId = getUserId(user);
  if (userId) {
    eventData.userId = userId;
  }
  if (method === "stepper") {
    sendEvent("register", eventData);
  } else if (method === "quick_register") {
    sendEvent("quickRegister", eventData);
  }

  console.log(
    `Analytics: Registration success tracked via ${method}`,
    userId ? `with userId: ${userId}` : "without userId",
  );
};

export const trackPageView = (path: string, customTitle?: string) => {
  const title = customTitle || getPageTitle(path);
  sendPageview(path, title);
  console.log(`Analytics: Page view tracked - ${path} (${title})`);
};

// Tenjin analytics metadata exposed by the iOS/Android native apps.
// `developer_device_id` is only provided on iOS.
interface TenjinAnalyticsMetadata {
  analytics_installation_id: string;
  advertising_id?: string;
  bundle_id: string;
  developer_device_id?: string;
  platform: string;
  os_version: string;
  sdk_version?: string;
}

// Guards against registering the device info more than once per page-load
// session, regardless of how many callers invoke registerDeviceInfo().
let deviceInfoRegistered = false;

// Registers the device's Tenjin analytics metadata with the backend.
// Only runs inside the iOS/Android webview (where the native bridge exists)
// and for authenticated users, since the endpoint requires a bearer token.
// Idempotent: safe to call from multiple places — it posts at most once.
export const registerDeviceInfo = async () => {
  try {
    if (deviceInfoRegistered) return;
    if (!getAccessToken()) return;

    const { os } = getUserAgentDetails();
    const ios = os.name === "iOS";
    const android = os.name?.includes("Android");

    const analytics = ios
      ? (globalThis as any).IOSAnalytics
      : android
        ? (globalThis as any).AndroidAnalytics
        : undefined;

    if (typeof analytics?.getTenjinAnalyticsMetadata !== "function") return;

    const raw = await analytics.getTenjinAnalyticsMetadata();
    const metadata: TenjinAnalyticsMetadata = typeof raw === "string" ? JSON.parse(raw) : raw;

    if (!metadata?.analytics_installation_id) return;

    // The Android bridge does not populate `platform` in its metadata payload
    // (it arrives null), while iOS does. Derive it from the detected OS so the
    // value is always present, and only fall back to the bridge value.
    const platform = ios ? "ios" : android ? "android" : metadata.platform;

    const deviceInfo: DeviceInfoRequest = {
      analyticsInstallationId: metadata.analytics_installation_id,
      advertisingId: metadata.advertising_id,
      developerDeviceId: metadata.developer_device_id,
      bundleId: metadata.bundle_id,
      platform,
      osVersion: metadata.os_version,
      sdkVersion: metadata.sdk_version,
    };

    deviceInfoRegistered = true;
    await Api.Device.saveDeviceInfo(deviceInfo);
    console.log("Analytics: Device info registered", deviceInfo);
  } catch (error) {
    console.error("Analytics: Failed to register device info", error);
  }
};
